import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowRight, Briefcase, Calendar, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { compRequest, CompensationServiceError } from '@/services/compensationService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ObChangePlanDialog', 'compensation');

interface SelectableSchedule {
  schedule_id: number;
  schedule_name: string;
  compensation_percentage: number | null;
  compensation_max_cap: number | null;
}

interface CurrentPlanSummary {
  scheduleId: number | null;
  scheduleName: string | null;
  percentage: number | null;
  maxCap: number | null;
  scope: 'core' | 'full_footprint' | 'custom_only' | 'mixed' | 'none' | null;
  effectiveDate: string | null;
}

interface ObChangePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  current: CurrentPlanSummary;
  onSubmitted?: () => void;
}

function formatMoney(n: number | null | undefined): string {
  if (n == null || n === 0) {
return 'No cap';
}
  return `$${n.toLocaleString()}`;
}

function formatPct(n: number | null | undefined): string {
  return n == null ? '—' : `${n}%`;
}

export function ObChangePlanDialog({
  open,
  onOpenChange,
  targetUserId,
  current,
  onSubmitted
}: ObChangePlanDialogProps): JSX.Element {
  const [schedules, setSchedules] = useState<SelectableSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Core-only — Lendwise's canonical workflow operates on the 14 standard core
  // categories. Legacy cached scope values ('full_footprint', 'custom_only')
  // are intentionally ineligible here; admin must Refresh from OB to re-derive.
  // The labels below stay permissive so legacy rows still render passively.
  const isEligibleScope = current.scope === 'core';

  useEffect(() => {
    if (!open) {
return;
}
    setSelectedScheduleId(null);
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('ob_comp_schedules')
        .select('schedule_id, schedule_name, compensation_percentage, compensation_max_cap')
        .eq('is_active', true)
        .eq('is_selectable_for_lo_self_service', true)
        .order('schedule_name');
      if (error) {
        log.error('Failed to load selectable schedules', {}, error);
        toast.error('Failed to load selectable plans');
        setLoading(false);
        return;
      }
      // Hide the user's CURRENT plan from the picker — they can't "change" to what they already have.
      setSchedules(((data ?? []) as SelectableSchedule[]).filter((s) => s.schedule_id !== current.scheduleId));
      setLoading(false);
    })();
  }, [open, current.scheduleId]);

  const selected = useMemo(
    () => schedules.find((s) => s.schedule_id === selectedScheduleId) ?? null,
    [schedules, selectedScheduleId]
  );

  const scopeBlurb = useMemo(() => {
    if (current.scope === 'core') {
      return 'This change will swap your 14 standard core categories. Any custom overlay assignments are preserved.';
    }
    // Legacy cached scopes ('full_footprint' / 'custom_only') and any non-core
    // current state — direct the admin to refresh first so the cache re-derives.
    return 'Plan change is unavailable until the OB plan is refreshed. Click "Refresh from OB" to re-derive the current state.';
  }, [current.scope]);

  // Effective date is always the 1st of next month in America/Los_Angeles. Compute
  // here for display so the user knows exactly when the change takes effect; the
  // edge function authoritatively recomputes it server-side at submit time.
  const effectiveDateDisplay = useMemo(() => {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'numeric'
    });
    const parts = fmt.formatToParts(new Date());
    const year = Number(parts.find((p) => p.type === 'year')!.value);
    const month = Number(parts.find((p) => p.type === 'month')!.value);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const date = new Date(nextYear, nextMonth - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, []);

  const scopeLabel = current.scope === 'full_footprint'
    ? 'Full Footprint'
    : current.scope === 'core'
      ? 'Core'
      : current.scope === 'custom_only'
        ? 'Custom Only'
        : current.scope === 'mixed'
          ? 'Mixed'
          : current.scope === 'none'
            ? 'Unlinked'
            : 'Unknown';

  const handleSubmit = async () => {
    if (!selectedScheduleId) {
      toast.error('Pick a plan');
      return;
    }
    setSubmitting(true);
    try {
      const result = await compRequest({
        targetUserId,
        targetScheduleId: selectedScheduleId
      });
      const planName = selected?.schedule_name ?? 'Plan change';
      toast.success(`${planName} scheduled for ${result.effectiveDate}`);
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Submit failed: ${msg}`);
      log.error('compRequest failed', { targetUserId, selectedScheduleId }, e as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Scrollable-dialog pattern:
        - max-h-[90dvh] uses dynamic viewport height so iOS Safari doesn't clip when the
          address bar collapses; falls back gracefully where dvh isn't supported.
        - flex flex-col + p-0 + gap-0 lets us partition the dialog into a fixed header,
          a scrolling body, and a fixed footer (keeps Submit/Cancel always reachable).
        - Mobile sizing: w-[calc(100vw-1rem)] keeps a small gutter on phones; sm:max-w-2xl
          opens up to a comfortable width on tablet+.
      */}
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[90dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 bg-gradient-to-br from-slate-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Change Compensation Plan
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 mt-1">
            <Calendar className="h-3.5 w-3.5" />
            Effective <span className="font-medium text-foreground">{effectiveDateDisplay}</span> · auto-scheduled, applied by the system
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-slate-50/50">
          {/* Before → After comparison */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-2 items-stretch">
            {/* Current plan card */}
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mb-2">
                <Briefcase className="h-3.5 w-3.5" />
                Current
              </div>
              <div className="font-semibold text-base mb-2">{current.scheduleName ?? 'Not set'}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-normal">{scopeLabel}</Badge>
                <span className="text-sm text-muted-foreground">
                  {formatPct(current.percentage)} · cap {formatMoney(current.maxCap)}
                </span>
              </div>
            </div>

            {/* Arrow connector — horizontal on desktop, hidden on mobile (the cards stack vertically) */}
            <div className="hidden sm:flex items-center justify-center text-muted-foreground">
              <ArrowRight className="h-5 w-5" />
            </div>

            {/* New plan preview card — empty state until picked */}
            <div
              className={
                selected
                  ? 'rounded-lg border-2 border-blue-500 bg-blue-50/50 p-4 shadow-sm transition-all'
                  : 'rounded-lg border-2 border-dashed border-muted-foreground/20 bg-white/40 p-4 transition-all'
              }
            >
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mb-2">
                {selected ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-blue-700">New</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    Pick below
                  </>
                )}
              </div>
              {selected ? (
                <>
                  <div className="font-semibold text-base mb-2">{selected.schedule_name}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-blue-600 hover:bg-blue-700 font-normal">{scopeLabel}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {formatPct(selected.compensation_percentage)} · cap {formatMoney(selected.compensation_max_cap)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground italic mt-1">No plan selected yet</div>
              )}
            </div>
          </div>

          {/* Scope blurb — what categories are affected */}
          {!isEligibleScope ? (
            <Alert variant="destructive">
              <AlertDescription>{scopeBlurb}</AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-900">{scopeBlurb}</AlertDescription>
            </Alert>
          )}

          {/* Plan picker */}
          {isEligibleScope && (
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <Label htmlFor="plan-select" className="text-sm font-semibold mb-2 block">
                Available plans
              </Label>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading plans…
                </div>
              ) : schedules.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No other plans are currently selectable. An admin needs to enable plans in the OB Plans tab.
                </p>
              ) : (
                <>
                  <Select
                    value={selectedScheduleId != null ? String(selectedScheduleId) : ''}
                    onValueChange={(v) => setSelectedScheduleId(Number(v))}
                  >
                    <SelectTrigger id="plan-select" className="w-full">
                      <SelectValue placeholder="Choose a plan…" />
                    </SelectTrigger>
                    <SelectContent>
                      {schedules.map((s) => (
                        <SelectItem key={s.schedule_id} value={String(s.schedule_id)}>
                          <span className="font-medium">{s.schedule_name}</span>
                          <span className="ml-2 text-muted-foreground text-sm">
                            · {formatPct(s.compensation_percentage)} · cap {formatMoney(s.compensation_max_cap)}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    {schedules.length} {schedules.length === 1 ? 'plan' : 'plans'} available
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/*
          Footer is sticky outside the scroll container so Submit/Cancel are always
          reachable. flex-col-reverse on mobile puts the primary action on top
          (above Cancel) — matches platform conventions where users tap the bottom
          button most readily on phones; sm:flex-row restores standard order on tablet+.
        */}
        <DialogFooter className="px-6 py-4 border-t shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!isEligibleScope || !selectedScheduleId || submitting}
            className="w-full sm:w-auto"
          >
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
